import { PlainDate } from "@kiss-solutions/plaindate";
import { DateTime } from "luxon";

const ONE_DAY = 1000 * 60 * 60 * 24;
const plus = (date, days) => new Date(date.getTime() + ONE_DAY * days);

const startOfDay = (date) => {
    date.setHours(0);
    return date;
};
const endOfDay = (date) => {
    date.setHours(24);
    return date;
};

export const convertZeitraum2Period = (zeitraum) => {
    return { von: startOfDay(new Date(zeitraum.start)), bis: endOfDay(new Date(zeitraum.end)) };
};

export const expandEndOneDay = (zeitraum) => {
    return {
        ...zeitraum,
        bis: plus(zeitraum.bis, 1),
    };
};

export const reduceIsoDateToDateString = (date) => date.substring(0, 10);

export const formatDateWithShortTime = (date, locale, timeZone) => date ?
    locale &&
    timeZone &&
    new Intl.DateTimeFormat(locale, { timeZone, dateStyle: "medium", timeStyle: "short" }).format(date) : null;

export const formationShortTime = (date, locale, timeZone) =>
    date && locale && timeZone && new Intl.DateTimeFormat(locale, { timeZone, timeStyle: "medium" }).format(date);

export const formatDate = (date, locale, timezone) => {
    if (!date) {
        return null;
    }
    const options = {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        timeZone: timezone,
    };
    return new Intl.DateTimeFormat(locale, options).format(date);
};

export const formatDateWithWeekday = (date, locale) => {
    const options = {
        weekday: "short",
        year: "numeric",
        month: "numeric",
        day: "numeric",
    };
    return new Intl.DateTimeFormat(locale, options).format(date);
};

export const formatDateTime = (date, locale, timezone) => {
    if (!date) {
        return null;
    }
    if (typeof date === "string") {
        date = new Date(date);
    }
    const options = {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric"
    };
    if (timezone) {
        options.timeZone = timezone;
    }
    return new Intl.DateTimeFormat(locale, options).format(date);
};

export const getNextMonday = () => {
    const newDate = new Date();
    if (newDate.getDay() === 1) {
        return newDate;
    }
    if (newDate.getDay() === 0) {
        return plus(newDate, 1);
    }
    return plus(newDate, 8 - newDate.getDay());
};

/**
 * Generic string-to-Date conversion for timestamp-like values.
 *
 * WARNING:
 * Do not use this for timezone-agnostic calendar-day fields like backend
 * `DatVon` / `DatBis` in artist locations. Use `toCalendarDayDate(...)`
 * instead so `"2026-01-01"` stays equal to a local JS Date on 1.1.2026.
 */
export const convertDateStringToDate = (dateString) => {
    if (!dateString) {
        return null;
    }
    if (typeof dateString === "string") {
        return new Date(dateString);
    }
    // check if it is a date
    if (Object.prototype.toString.call(dateString) === "[object Date]") {
        return dateString;
    }
    return null;
};

/**
 * Calendar-day values are timezone-agnostic in this app.
 *
 * Contract:
 * - backend date strings like "2026-01-01" or "2026-01-01T00:00:00"
 * - local JS Date objects representing the same local calendar day
 *
 * must normalize to the same local calendar date without timezone shifting.
 *
 * IMPORTANT:
 * Do not use `new Date("YYYY-MM-DD")` for these values. Native parsing applies
 * UTC/local timezone semantics and can shift the calendar day by one.
 */
export const toCalendarDayDate = (value) => {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "string") {
        try {
            const parsed = new PlainDate(value.substring(0, 10)).toLocalDate();
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        } catch {
            return null;
        }
    }
    return null;
};

/**
 * Returns a timezone-agnostic local calendar day string in `YYYY-MM-DD`.
 * Use this for backend date-only fields and dialog dates that represent a
 * calendar day rather than an absolute instant.
 */
export const toCalendarDayString = (value) => {
    const date = toCalendarDayDate(value);
    return date ? toDateString(date) : null;
};

export const isAfterByDate = (date1, date2) => {
    const date1Count = date1.getFullYear() * 365 + date1.getMonth() * 30 + date1.getDate();
    const date2Count = date2.getFullYear() * 365 + date2.getMonth() * 30 + date2.getDate();
    return date1Count > date2Count;
};

function pad(num, size) {
    num = num.toString();
    while (num.length < size) num = "0" + num;
    return num;
}

export const toDateString = (date) =>
    date ? pad(date.getFullYear(), 4) + "-" + pad(date.getMonth() + 1, 2) + "-" + pad(date.getDate(), 2) : null;

export const toTimeString = (date) =>
    pad(date.getHours(), 2) + ":" + pad(date.getMinutes(), 2) + ":" + pad(date.getSeconds(), 2);

export const toLocalDateTimeStringInIsoFormat = (date) => {
    if (!date) {
        return null;
    }
    return toDateString(date) + "T" + toTimeString(date);
};

export const convertIsoToDateStringWithTimezone = (isoString, timezone) => {
    if (!isoString || !timezone) {
        return null;
    }
    return DateTime.fromISO(isoString).setZone(timezone).toFormat("yyyy-MM-dd");
};

export const convertIsoStringToLocalDateTimeString = (isoString) => isoString.replace("T", " ").substring(0, 19);

/**
 * Converts a Date object to a timezone-agnostic local datetime string in format "YYYY-MM-DD HH:mm:ss".
 * This function extracts the local date/time components without any timezone conversion,
 * which is required for calendar events that must represent studio local time.
 * 
 * @param {Date} date - Date object to convert
 * @returns {string|null} Local datetime string in format "YYYY-MM-DD HH:mm:ss" or null if date is invalid
 */
export const toLocalDateTimeString = (date) => {
    if (!date) {
        return null;
    }
    return toDateString(date) + " " + toTimeString(date);
};
